import React from 'react';
import Editor from '@monaco-editor/react';

interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language?: string;
  height?: string;
  readOnly?: boolean;
}

const DEFAULT_SPOT_SCRIPT = `// Scripted Spot Runner
// Available globals:
//   api - Trading API methods
//   runner - Runner info (symbol, baseCurrency, investment, intervalHours)
//   config - Parsed ConfigJson object
//   state - Mutable state object (persisted to database)

// Called when the runner starts
function onStart() {
  api.Log("Runner started for " + runner.symbol);

  // Initialize state if needed
  if (!state.initialized) {
    state.initialized = true;
    state.lastPurchaseTime = null;
    state.totalBought = 0;
  }
}

// Called on each price update
function onTick(tick) {
  // tick has: bestBidPrice, bestAskPrice, timestamp

  // Example: Buy $10 worth every 24 hours
  var hoursSinceLast = state.lastPurchaseTime
    ? api.HoursSince(new Date(state.lastPurchaseTime))
    : 999;

  if (hoursSinceLast >= 24) {
    var orderId = api.BuyAmount(10, "Scripted DCA buy");
    if (orderId) {
      state.lastPurchaseTime = api.Now().toISOString();
      api.SaveState();
    }
  }
}

// Called when an order is filled
function onOrderUpdate(order) {
  // order has: orderId, status, side, filledQuantity, matchPrice, isFilled
  if (order.isFilled && order.side === "Buy") {
    state.totalBought += order.filledQuantity;
    api.Log("Bought " + order.filledQuantity + " @ " + order.matchPrice);
    api.SaveState();
  }
}

// Called on bar close (if bar data is available)
function onBarClose(bar) {
  // bar has: open, high, low, close, volume, timestamp
}
`;

const DEFAULT_FUTURES_SCRIPT = `// Scripted Futures Runner
// Available globals:
//   api - Trading API methods
//   runner - Runner info (symbol, baseCurrency, investment, intervalHours)
//   config - Parsed ConfigJson object
//   state - Mutable state object (persisted to database)

// Called when the runner starts
function onStart() {
  api.Log("Futures runner started for " + runner.symbol);

  // Initialize state if needed
  if (!state.initialized) {
    state.initialized = true;
    state.position = 0;
    state.entryPrice = 0;
  }
}

// Called on each price update
function onTick(tick) {
  // tick has: bestBidPrice, bestAskPrice, timestamp
  var price = tick.bestAskPrice;

  // Example: Simple momentum strategy
  if (!state.lastPrice) {
    state.lastPrice = price;
    return;
  }

  var priceChange = (price - state.lastPrice) / state.lastPrice * 100;
  state.lastPrice = price;

  // If price moved up more than 0.5% and we're not long, go long
  if (priceChange > 0.5 && state.position <= 0) {
    if (state.position < 0) {
      api.CloseShort(Math.abs(state.position));
    }
    var qty = runner.investment / price;
    api.Long(qty, 1, "Momentum long");
  }
  // If price moved down more than 0.5% and we're not short, go short
  else if (priceChange < -0.5 && state.position >= 0) {
    if (state.position > 0) {
      api.CloseLong(state.position);
    }
    var qty = runner.investment / price;
    api.Short(qty, 1, "Momentum short");
  }
}

// Called when an order is filled
function onOrderUpdate(order) {
  // order has: orderId, status, side, filledQuantity, matchPrice, isFilled
  if (order.isFilled) {
    if (order.side === "Buy") {
      state.position += order.filledQuantity;
    } else {
      state.position -= order.filledQuantity;
    }
    state.entryPrice = order.matchPrice;
    api.Log("Position now: " + state.position);
    api.SaveState();
  }
}

// Called on bar close (if bar data is available)
function onBarClose(bar) {
  // bar has: open, high, low, close, volume, timestamp
}
`;

export function getDefaultScript(runnerType: 'Spot' | 'Futures'): string {
  return runnerType === 'Futures' ? DEFAULT_FUTURES_SCRIPT : DEFAULT_SPOT_SCRIPT;
}

export function CodeEditor({
  value,
  onChange,
  language = 'javascript',
  height = '400px',
  readOnly = false
}: CodeEditorProps) {
  return (
    <div style={styles.container}>
      <Editor
        height={height}
        language={language}
        value={value}
        onChange={(val) => onChange(val || '')}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: 2,
          readOnly,
          wordWrap: 'on',
          folding: true,
          renderLineHighlight: 'all',
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
          },
        }}
      />
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    border: '1px solid #233554',
    borderRadius: '8px',
    overflow: 'hidden',
  },
};
